/**
 * The surfacer "Only unjudged" queue preference, per user.
 *
 * On: the worklist hides, and Next case skips, a surfacer study once every one of
 * its findings has a verdict from someone in the version family (see
 * `useSurfacerReviewState`). Off: the queue goes by the reader's own verdicts
 * only, as before.
 *
 * Default is on for internal staff, who share one pool of verdicts, and off for
 * everyone else. A choice is kept in localStorage under the user's id, so two
 * people on one browser don't share it. Storage can be unavailable (private
 * mode, blocked site data), so every read and write is guarded and the default
 * applies when it is. A session atom carries the choice between the worklist and
 * the case viewer so both see a change at once.
 */

import { atom, useAtom, useAtomValue } from "jotai";
import { isAdminAtom, userIdAtom, userTypeAtom } from "../atoms/authAtoms";
import { isInternalRole } from "../services/roles";

const STORAGE_PREFIX = "surfacerOnlyUnjudged:";

/** userId → choice made this session. Storage is the fallback, then the default. */
const sessionChoiceAtom = atom<Record<string, boolean>>({});

export function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export function readStoredChoice(userId: string): boolean | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    return raw === "true" ? true : raw === "false" ? false : null;
  } catch {
    return null;
  }
}

function writeStoredChoice(userId: string, value: boolean): void {
  try {
    window.localStorage.setItem(storageKey(userId), String(value));
  } catch {
    // Storage unavailable: the choice still holds for this session.
  }
}

export function useOnlyUnjudged(): {
  onlyUnjudged: boolean;
  setOnlyUnjudged: (value: boolean) => void;
} {
  const userId = useAtomValue(userIdAtom) ?? "";
  const isAdmin = useAtomValue(isAdminAtom);
  const userType = useAtomValue(userTypeAtom);
  const [session, setSession] = useAtom(sessionChoiceAtom);
  const defaultOn = isAdmin || isInternalRole(userType);
  const onlyUnjudged = session[userId] ?? readStoredChoice(userId) ?? defaultOn;
  return {
    onlyUnjudged,
    setOnlyUnjudged: (value: boolean) => {
      setSession((prev) => ({ ...prev, [userId]: value }));
      writeStoredChoice(userId, value);
    },
  };
}
